import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Select } from "antd";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import Image from "../../../Assets/Icons/Image.svg";
import { getFundraiserReferrals } from "../../../services/fundraiserService";
import { useDispatch } from "react-redux";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import "./dashboard.scss";

export default function ReferredMerchants() {
  const dispatch = useDispatch();
  const [selectedFilter, setSelectedFilter] = useState("Year");

  const [referredMerchants, setReferredMerchants] = useState([]);
  const [pendingMerchants, setPendingMerchants] = useState([]);
  const [acceptedMerchants, setAcceptedMerchants] = useState([]);
  const getMerchantsData = async () => {
    const merchantsPromise = await dispatch(getFundraiserReferrals())?.unwrap();
    if (merchantsPromise?.success === true) {
      setReferredMerchants(merchantsPromise?.data);
      setPendingMerchants(merchantsPromise?.totalPendingReferredMerchants);
      setAcceptedMerchants(merchantsPromise?.totalAcceptedReferredMerchants);
    }
  };

  useEffect(() => {
    getMerchantsData();
  }, []);

  // Summary statistics
  const totalInvites = referredMerchants?.length;
  const pending = pendingMerchants;
  const accepted = acceptedMerchants;

  return (
    <div className="w-full h-[400px] summary bg-white p-4">
      <div className="flex justify-between items-start mb-2">
        <h1 className="text-[16px] md:text-[18px] lg:text[20px] font-[600] text-[#303030] font-poppins">
          Referred Merchants
        </h1>
      </div>

      <div className="flex flex-wrap gap-4 space-x-0 md:space-x-8 md:gap-0 mb-8">
        <div className="flex items-center text-[12px] md:text-[14px] font-poppins">
          <span className="font-[700] text-[#000000]">Total Invites:</span>
          <span className="ml-2 text-[#8C8C8C] font-[400]">{totalInvites}</span>
        </div>
        <div className="flex items-center text-[12px] md:text-[14px] font-poppins">
          <span className="font-[700] text-[#000000]">Pending:</span>
          <span className="ml-2 text-[#8C8C8C] font-[400]">{pending}</span>
        </div>
        <div className="flex items-center text-[12px] md:text-[14px] font-poppins">
          <span className="font-[700] text-[#000000]">Accepted:</span>
          <span className="ml-2 text-[#8C8C8C] font-[400]">{accepted}</span>
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden h-[250px] overflow-y-auto pr-1">
        {referredMerchants.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center py-10">
            <p className="text-sm font-medium text-gray-400">
              No referred merchants yet!
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {referredMerchants.map((merchant) => (
              <div
                key={merchant._id}
                className="bg-white border border-[#E5E7EB] rounded-lg p-4 shadow-sm flex justify-between items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-full w-full flex items-center justify-center rounded-full object-contain overflow-hidden bg-gray-50">
                      <img
                        src={
                          merchant?.merchant?.logo
                            ? process.env.REACT_APP_S3_URL +
                            merchant?.merchant?.logo
                            : Image
                        }
                        alt="merchant logo"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[14px] font-poppins font-[600] text-[#303030]">
                      {merchant?.name}
                    </span>
                    <span className="text-[12px] font-poppins font-[400] text-[#8C8C8C]">
                      {
                        separateDateTimeAndConvertToLocale(merchant.createdAt)
                          ?.localDate
                      }
                    </span>
                  </div>
                </div>
                <div>
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-poppins font-[600] ${merchant.status === "pending"
                        ? "bg-[#ECC044] text-white"
                        : "bg-[#4CAC5B] text-white"
                      }`}
                  >
                    {merchant.status.charAt(0).toUpperCase() +
                      merchant.status.slice(1)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden md:block">
        <TableContainer
          component={Paper}
          elevation={0}
          className="h-[250px] overflow-y-auto"
        >
          {referredMerchants.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center py-10">
              <p className="text-sm font-medium text-gray-400">
                No referred merchants yet!
              </p>
            </div>
          ) : (
            <Table
              sx={{ minWidth: 400 }}
              aria-label="referred merchants table"
              className="referred"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: 500, color: "#4B5563" }}
                    className="!text-[12px] md:!text-[14px] !font-poppins !font-[700] !text-[#000000] max-w-[50px]"
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, color: "#4B5563" }}
                    className="!text-[12px] md:!text-[14px] !font-poppins !font-[700] !text-[#000000] max-w-[50px]"
                  >
                    Merchant
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 500, color: "#4B5563" }}
                    className="!text-[12px] md:!text-[14px] !font-poppins !font-[700] !text-[#000000] max-w-[50px]"
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referredMerchants.map((merchant) => (
                  <TableRow
                    key={merchant._id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ color: "#4B5563" }}
                      className="!text-[12px] md:!text-[14px] !font-poppins !font-[400] !text-[#8C8C8C]"
                    >
                      {
                        separateDateTimeAndConvertToLocale(merchant.createdAt)
                          ?.localDate
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 mr-3">
                          <div className="h-full w-full flex items-center justify-center rounded-full object-contain">
                            <img
                              src={
                                merchant?.merchant?.logo
                                  ? process.env.REACT_APP_S3_URL +
                                  merchant?.merchant?.logo
                                  : Image
                              }
                            />
                          </div>
                        </div>
                        <div className="!text-[12px] md:!text-[14px] !font-poppins !font-[400] !text-[#8C8C8C]">
                          {merchant?.name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`px-4 py-1 rounded-full !text-[10px] md:!text-[12px] !font-poppins !font-[600]" ${merchant.status === "pending"
                            ? "bg-[#ECC044] !text-white"
                            : "bg-[#4CAC5B] !text-white"
                          }`}
                      >
                        {merchant.status.charAt(0).toUpperCase() +
                          merchant.status.slice(1)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </div>
    </div>
  );
}
